/* eslint-disable react-refresh/only-export-components */
import { ReactNode, createContext, memo, useContext, useState, useCallback } from 'react'

interface ContextData {
  icon?: ReactNode
  title: string | ReactNode
  content: ReactNode
  closeText: string
  size?: 'default' | 'large'
  customActions?: ReactNode
  justifiedButtons?: boolean
}

interface Output {
  isOpen: boolean
  open: () => void
  close: () => void
  data?: ContextData
  setData: (data: ContextData) => void
}

export const DialogContext = createContext<Output | null>(null)

interface HookOutput {
  open: () => void
  close: () => void
}

interface HookInput {
  icon?: ReactNode
  title: string | ReactNode
  content: ReactNode
  closeText?: string
  size?: 'default' | 'large'
  customActions?: ReactNode
  justifiedButtons?: boolean
}

export function useDialog({
  icon,
  title,
  content,
  closeText = 'OK',
  size = 'default',
  customActions,
  justifiedButtons = false,
}: HookInput): HookOutput {
  const context = useContext(DialogContext)
  if (!context) {
    throw new Error('useDialog deve ser chamado dentro do DialogContext.Provider!')
  }
  const { open: contextOpen, close: contextClose, setData } = context

  const open = useCallback(() => {
    setData({ icon, title, content, closeText, size, customActions, justifiedButtons })
    contextOpen()
  }, [setData, contextOpen, icon, title, content, closeText, size, customActions, justifiedButtons])

  const close = useCallback(() => {
    contextClose()
  }, [contextClose])

  return { open, close }
}

export const ContextProvider = memo(ContextProviderComponent)

function ContextProviderComponent({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [data, setData] = useState<ContextData | undefined>(undefined)

  const openDialog = useCallback(() => setIsOpen(true), [])
  const closeDialog = useCallback(() => setIsOpen(false), [])

  const contextValue = useCallback(
    (): Output => ({
      isOpen,
      open: openDialog,
      close: closeDialog,
      data,
      setData,
    }),
    [isOpen, openDialog, closeDialog, data],
  )

  return <DialogContext.Provider value={contextValue()}>{children}</DialogContext.Provider>
}
